import { useState } from "react";
import useTooltip from "../../../../hooks/useTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShare,
  faSpinner,
  faThumbsUp as faThumbsUpRegular,
  faComment,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faLock,
  faThumbsUp as faThumbsUpSolid,
} from "@fortawesome/pro-solid-svg-icons";
import classNames from "classnames";
import { useMutation } from "react-query";
import { fetchApi } from "../../../../helpers/reactQueryApi";
import { filter, get, isEmpty, omit, pick } from "lodash";
import LikesModal from "./LikesModal";
import {
  ActivityReactionBadges,
  ActivityReactionButton,
} from "./ActivityReactions";
import { useUpdateActivityInCache } from "../../../../hooks/activitystreams/useUpdateActivityInCache";
import {
  Activity,
  ActivityLike,
  ActivityObject,
  Reaction,
} from "../../../../@types/activities.ts";
import TranslationControls from "@/components/shared/TranslationControls.tsx";
import ActivityContextMenu from "@/components/activitystreams/activity/ActivityContextMenu.tsx";
import ReachStatistic from "@/components/shared/ReachStatistic.tsx";
import { Activitystream } from "@/api/activitystreams.ts";
import { useLongPress } from "@/hooks/useLongPress.ts";

type InteractionsArgs = {
  activityId: string;
  likeCount: number | null;
  hasAttachments: boolean;
  likes: ActivityLike[] | null;
  shareActivity: () => void;
  type: ActivityObject["type"];
  translateObject: (
    data: { [key: string]: string },
    language: string,
    type?: string,
  ) => void;
  resetTranslation: (language: string) => void;
  translatedContent: {
    language?: string;
    content?: string;
    name?: string;
    description?: string;
  };
  can: Activity["can"];
  activityObject: ActivityObject;
  emailRecommendationCount: { internal: number; external: number };
  emailRecommendationInfo: {
    hasAttachments: boolean;
    totalAttachmentSize: number;
    lastRecommendation?: Activity["email_recommendation"];
  };
  plannedForNewsletterAt: Activity["planned_for_newsletter_at"];
  iSubscribedEmail: Activity["i_subscribed_email"];
  actor: Activity["actor"];
  repliesCount: number;
  streamsFromActivity?: Activitystream[];
  iLiked?: boolean;
  canLike?: boolean;
  reactions?: Reaction[] | null;
  myReaction?: string | null;
  canReact?: boolean;
  likeListShort?: string | null;
  likeListLong?: string | null;
  shareable?: boolean;
  closed?: boolean;
  error?: boolean;
  repliesClosed?: Activity["replies_closed"];
  teaser?: string;
  sticky?: Activity["sticky"];
  archivedAt?: Activity["archived_at"];
  streamUrl?: string;
  toggleReplies?: () => void;
  readOnly?: boolean;
  viewCount?: number | null;
  emailDeliveryCount?: number | null;
  reachCount?: number | null;
};

export default function Interactions({
  activityId,
  likeListShort,
  likeListLong,
  iLiked,
  likeCount,
  canLike,
  reactions,
  myReaction,
  canReact,
  shareable,
  hasAttachments,
  likes,
  shareActivity,
  closed,
  type,
  translatedContent,
  resetTranslation,
  translateObject,
  error,
  can,
  activityObject,
  emailRecommendationCount,
  emailRecommendationInfo,
  streamsFromActivity,
  repliesClosed,
  teaser,
  plannedForNewsletterAt,
  sticky,
  archivedAt,
  iSubscribedEmail,
  streamUrl,
  actor,
  repliesCount,
  toggleReplies,
  readOnly,
  viewCount,
  emailDeliveryCount,
  reachCount,
}: InteractionsArgs) {
  const toolTipRef = useTooltip<HTMLButtonElement>();
  const [showLikes, setShowLikes] = useState(false);
  const { updateActivity } = useUpdateActivityInCache();

  const { mutate: performLike, isLoading: isUpdatingLike } = useMutation(
    () =>
      fetchApi(`/activities/${activityId}/${iLiked ? "unlike" : "like"}.json`, {
        method: "POST",
      }),
    {
      onSuccess: (data) => {
        if (data) {
          updateActivity(activityId, (activity) => ({
            ...activity,
            object: {
              ...activity.object,
              ...data.objects_meta,
              my_like: iLiked ? null : omit(data, "objects_meta"),
              likes: activity.object.likes
                ? iLiked
                  ? filter(likes, (like) => like.id !== data.id)
                  : [...(likes || []), omit(data, "objects_meta")]
                : [],
              like_count: iLiked ? (likeCount || 1) - 1 : (likeCount || 0) + 1,
            },
          }));
        }
      },

      onError: () => {
        updateActivity(activityId, (activity) => ({
          ...activity,
          object: {
            ...activity.object,
            my_like: activity.object.my_like,
            like_count: activity.object.like_count,
          },
        }));
      },
    },
  );

  const reactionsOn =
    Preload.current_network.config?.activities?.reactions_enabled === true;

  // Toggle/replace semantics: tapping your current emoji removes it, any other
  // emoji replaces it. The server returns the updated object; we reconcile the
  // reactions block into the cache (server is source of truth).
  const { mutate: toggleReaction } = useMutation(
    (emoji: string) =>
      fetchApi(
        `/activities/${activityId}/${myReaction === emoji ? "unreact" : "react"}.json`,
        myReaction === emoji
          ? { method: "POST" }
          : { method: "POST", body: { emoji } },
      ),
    {
      onSuccess: (data) => {
        if (!data) return;
        updateActivity(activityId, (activity) => ({
          ...activity,
          object: {
            ...activity.object,
            ...pick(data, ["reactions", "my_reaction"]),
          },
        }));
      },
    },
  );

  const handlers = useLongPress(
    () => setShowLikes(true),
    () => performLike(),
  );
  const buttonEvents = window.isApp ? handlers : {};

  return (
    <>
      {showLikes && likes && (
        <LikesModal likes={likes} close={() => setShowLikes(false)} />
      )}
      <div
        className={classNames("activity-interaction flex flex-col gap-2 pb-2", {
          "border-t border-neutral border-dashed":
            !hasAttachments && !window.isApp,
          "border-t border-neutral/25 border-solid":
            !hasAttachments && window.isApp,
        })}
      >
        {closed && (
          <span className="activity-interaction-meta flex gap-1 items-baseline text-sm text-normal mb-4">
            <FontAwesomeIcon icon={faLock} />
            {I18n.t(`js.activitystreams.${type}_closed`)}
          </span>
        )}
        {reactionsOn && !isEmpty(reactions) && (
          <div className="activity-reactions flex flex-wrap items-center gap-1 py-1">
            <ActivityReactionBadges
              reactions={reactions}
              onSelect={toggleReaction}
            />
          </div>
        )}
        {!window.isApp && !reactionsOn && (
          <div>
            {isUpdatingLike ? (
              <FontAwesomeIcon className="text-muted" icon={faSpinner} spin />
            ) : (
              !isEmpty(likeListShort) && (
                <button
                  className="btn text-normal text-sm hover:underline hover:text-primary px-0"
                  role="button"
                  ref={toolTipRef}
                  title={likeListLong || undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowLikes(true);
                  }}
                >
                  <span className="like-list-short">{likeListShort}</span>
                </button>
              )
            )}
          </div>
        )}
        <div className="interactions flex gap-2">
          {reactionsOn && canReact && (
            <ActivityReactionButton
              myReaction={myReaction}
              onSelect={toggleReaction}
            />
          )}
          {!reactionsOn && canLike && (
            <button
              className={classNames("btn btn-sm", {
                "btn-light": !window.isApp,
                active: iLiked,
              })}
              role="button"
              onClick={
                !window.isApp
                  ? (e) => {
                      e.preventDefault();
                      performLike();
                    }
                  : undefined
              }
              {...buttonEvents}
            >
              <FontAwesomeIcon
                className="mr-1 text-primary"
                icon={
                  window.isApp && isUpdatingLike
                    ? faSpinner
                    : iLiked
                      ? faThumbsUpSolid
                      : faThumbsUpRegular
                }
                size={window.isApp ? "xl" : undefined}
                spin={window.isApp && isUpdatingLike}
              />
              {!window.isApp && I18n.t("js.activity.likes.title")}
              {window.isApp && likeCount}
            </button>
          )}
          {window.isApp && !readOnly && (
            <button
              type="button"
              className="btn"
              onClick={() => toggleReplies?.()}
            >
              <FontAwesomeIcon
                className="mr-1 text-primary"
                icon={faComment}
                size="xl"
              />
              {repliesCount}
            </button>
          )}
          {shareable && (
            <button
              className={classNames("btn btn-sm", {
                "btn-light": !window.isApp,
              })}
              role="button"
              onClick={(e) => {
                e.preventDefault();
                shareActivity();
              }}
            >
              <FontAwesomeIcon
                className="mr-1 text-primary"
                icon={faShare}
                size={window.isApp ? "xl" : undefined}
              />
              {!window.isApp && I18n.t("js.activitystreams.share")}
            </button>
          )}
          {Preload.current_network.config?.translations?.deep_l?.enabled &&
            !error &&
            type !== "article" &&
            window.isApp && (
              <TranslationControls
                object_language={translatedContent.language || "ORIGINAL"}
                types={["activity"]}
                target_id={activityId}
                resetTranslation={resetTranslation}
                translateObject={translateObject}
              />
            )}
          {viewCount != null && (
            <ReachStatistic
              viewCount={viewCount}
              emailDeliveryCount={emailDeliveryCount ?? 0}
              reachCount={reachCount ?? viewCount}
            />
          )}
          {!error && window.isApp && (
            <ActivityContextMenu
              activityId={activityId}
              emailRecommendationCount={emailRecommendationCount}
              emailRecommendationInfo={emailRecommendationInfo}
              streamsFromActivity={streamsFromActivity}
              repliesClosed={repliesClosed}
              teaser={teaser}
              activityCan={can}
              objectId={activityObject?.id}
              plannedForNewsletterAt={plannedForNewsletterAt}
              sticky={sticky}
              archivedAt={archivedAt}
              canShare={shareable}
              shareActivity={shareActivity}
              iSubscribedEmail={iSubscribedEmail}
              streamUrl={streamUrl}
              closable={get(activityObject, "closable?")}
              iBookmarked={get(activityObject, "i_bookmarked?")}
              iRecommend={get(activityObject, "i_recommend?")}
              actor={actor}
              {...(omit(activityObject, [
                "id",
                "closable?",
                "i_bookmarked?",
                "i_recommend?",
              ]) as Omit<
                ActivityObject,
                "id" | "closable?" | "i_bookmarked?" | "i_recommend?"
              >)}
            />
          )}
        </div>
      </div>
    </>
  );
}
